import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  Film, Type, Captions, Shapes, Image as ImageIcon, Video, AudioLines, Sparkles,
  Bot, ChevronDown, Languages, FileText, Layers, SlidersHorizontal, Palette,
  ArrowUp, ArrowDown, Trash2, Wand2, Frame,
} from "lucide-react";

// Content-first CONTEXTUAL ribbon. Constant category tabs on the left always answer
// "what can I add?"; the right cluster answers "what can I do with what I selected?".
// Detailed configuration never lives here — it opens as a floating popup.
export default function EditorRibbon({ selectionKind = null, actions = {}, onOpenPopup }) {
  const [open, setOpen] = useState(null);
  const [menuPos, setMenuPos] = useState(null); // fixed-position anchor for the portaled dropdown
  const ref = useRef(null);
  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && ref.current.contains(e.target)) return;
      if (e.target.closest && e.target.closest("[data-ribbon-portal-menu]")) return;
      setOpen(null);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(null); };
    window.addEventListener("mousedown", onDoc);
    window.addEventListener("keydown", onKey);
    return () => { window.removeEventListener("mousedown", onDoc); window.removeEventListener("keydown", onKey); };
  }, []);
  const a = actions;
  const pop = (n) => () => { setOpen(null); onOpenPopup && onOpenPopup(n); };
  const run = (fn) => () => { setOpen(null); fn && fn(); };
  const openMenu = (e, key, isOpen) => {
    if (isOpen) { setOpen(null); return; }
    const r = e.currentTarget.getBoundingClientRect();
    setMenuPos({ left: Math.round(r.left), top: Math.round(r.bottom + 4) });
    setOpen(key);
  };

  const TABS = [
    { key: "media", label: "Media", icon: Film, onClick: pop("media") },
    { key: "text", label: "Text", icon: Type, onClick: run(a.addText) },
    { key: "captions", label: "Captions", icon: Captions, menu: [
      { label: "Transcribe", icon: FileText, onClick: run(a.transcribe), testid: "ribbon-transcribe" },
      { label: "Create Captions", icon: Captions, onClick: run(a.createCaptions), testid: "ribbon-create-captions" },
      { label: "Translate", icon: Languages, onClick: run(a.translate), testid: "ribbon-translate" },
      { label: "Transliterate", icon: Languages, onClick: run(a.transliterate), testid: "ribbon-transliterate" },
      { label: "Caption Presets", icon: Sparkles, onClick: pop("presets"), testid: "ribbon-caption-presets" },
    ] },
    { key: "elements", label: "Elements", icon: Shapes, onClick: pop("elements") },
    { key: "background", label: "Background", icon: Palette, onClick: pop("background") },
    { key: "image", label: "Image", icon: ImageIcon, kind: "image", onClick: pop("details") },
    { key: "video", label: "Video", icon: Video, kind: "video", onClick: pop("details") },
    { key: "audio", label: "Audio", icon: AudioLines, kind: "audio", onClick: pop("details") },
    { key: "presets", label: "Presets", icon: Palette, onClick: pop("presets") },
    { key: "animation", label: "Animation", icon: Sparkles, onClick: run(a.openAnimation) },
    { key: "ai", label: "AI", icon: Bot, menu: [
      { label: "Transcribe", icon: FileText, onClick: run(a.transcribe), testid: "ribbon-ai-transcribe" },
      { label: "Translate", icon: Languages, onClick: run(a.translate), testid: "ribbon-ai-translate" },
      { label: "Transliterate", icon: Languages, onClick: run(a.transliterate), testid: "ribbon-ai-transliterate" },
      { label: "Remove Background", icon: Wand2, onClick: pop("details"), testid: "ribbon-ai-removebg" },
    ] },
  ];

  const CTX = {
    caption: { label: "Caption", color: "#00F0FF", actions: ["props", "preset", "anim", "layer", "del"] },
    image: { label: "Image", color: "#FFC53D", actions: ["props", "preset", "anim", "del"] },
    video: { label: "Video", color: "#8B5CF6", actions: ["props", "shape", "del"] },
    audio: { label: "Audio", color: "#00F0FF", actions: ["props", "del"] },
    element: { label: "Element", color: "#EC4899", actions: ["props", "del"] },
  }[selectionKind];

  return (
    <div ref={ref} data-testid="editor-ribbon"
      className="relative z-40 flex h-12 shrink-0 items-center gap-0.5 overflow-x-auto border-b border-white/10 bg-[#0D0D0E] px-2">
      {TABS.map((t) => {
        const Icon = t.icon;
        const active = t.kind && t.kind === selectionKind;
        const isOpen = open === t.key;
        return (
          <div key={t.key} className="relative shrink-0">
            <button data-testid={`top-ribbon-tab-${t.key}`}
              onClick={(e) => (t.menu ? openMenu(e, t.key, isOpen) : t.onClick())}
              className={`flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-semibold transition-colors ${
                active ? "bg-[#1E1E24] text-[#00F0FF]" : isOpen ? "bg-[#1E1E24] text-white" : "text-[#A1A1AA] hover:bg-white/5 hover:text-white"}`}>
              <Icon size={15} /><span className="hidden lg:inline">{t.label}</span>
              {t.menu && <ChevronDown size={12} className="opacity-60" />}
              {active && <span className="ml-0.5 h-1.5 w-1.5 rounded-full bg-[#00F0FF]" />}
            </button>
          </div>
        );
      })}

      {CTX && (
        <div className="ml-auto flex shrink-0 items-center gap-1.5 rounded-lg border border-white/10 bg-white/[0.03] px-1.5 py-1" data-testid="ribbon-context-cluster">
          <span className="rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide" style={{ color: CTX.color, background: `${CTX.color}22` }}>{CTX.label}</span>
          {CTX.actions.includes("props") && (
            <button data-testid="ctx-properties-btn" onClick={pop("details")} title="Properties"
              className="flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-semibold text-white hover:bg-white/10"><SlidersHorizontal size={13} /> Properties</button>)}
          {CTX.actions.includes("preset") && (
            <button data-testid="ctx-preset-btn" onClick={pop("presets")} title="Presets"
              className="flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-semibold text-[#a78bfa] hover:bg-white/10"><Layers size={13} /> Preset</button>)}
          {CTX.actions.includes("anim") && (
            <button data-testid="ctx-animation-btn" onClick={run(a.openAnimation)} title="Animation"
              className="flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-semibold text-white hover:bg-white/10"><Sparkles size={13} /> Animation</button>)}
          {CTX.actions.includes("shape") && (
            <button data-testid="ctx-shape-btn" onClick={pop("shape")} title="Shape"
              className="flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-semibold text-[#c4b5fd] hover:bg-white/10"><Frame size={13} /> Shape</button>)}
          {CTX.actions.includes("layer") && (<>
            <button data-testid="ctx-layer-up-btn" onClick={run(a.moveLayerUp)} title="Move layer up" className="rounded-md p-1 text-[#A1A1AA] hover:bg-white/10 hover:text-[#00F0FF]"><ArrowUp size={14} /></button>
            <button data-testid="ctx-layer-down-btn" onClick={run(a.moveLayerDown)} title="Move layer down" className="rounded-md p-1 text-[#A1A1AA] hover:bg-white/10 hover:text-[#00F0FF]"><ArrowDown size={14} /></button>
          </>)}
          {CTX.actions.includes("del") && (
            <button data-testid="ctx-delete-btn" onClick={run(a.deleteSelection)} title="Delete selection"
              className="rounded-md p-1 text-[#A1A1AA] hover:bg-white/10 hover:text-red-400"><Trash2 size={14} /></button>)}
        </div>
      )}
      {open && menuPos && (() => {
        const t = TABS.find((x) => x.key === open && x.menu);
        if (!t) return null;
        return createPortal(
          <div data-ribbon-portal-menu data-testid={`ribbon-menu-${t.key}`}
            style={{ position: "fixed", left: menuPos.left, top: menuPos.top, zIndex: 2147483000 }}
            className="w-52 rounded-lg border border-white/10 bg-[#141418]/98 p-1.5 shadow-2xl backdrop-blur cm-pop-in">
            {t.menu.map((it) => { const It = it.icon; return (
              <button key={it.label} data-testid={it.testid} onClick={it.onClick}
                className="flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-xs font-medium text-[#D4D4D8] transition-colors hover:bg-[#252530] hover:text-white">
                <It size={14} className="text-[#00F0FF]" /> {it.label}
              </button>); })}
          </div>, document.body);
      })()}
    </div>
  );
}
